import { amountDueOf, isOverdue, isPayable, isUnpaid, needsMySignature } from './outstanding';
import type { OutstandingItem } from './outstanding.schema';

/**
 * Outstanding work, grouped by the company it belongs to.
 *
 * A director does not think in rows. They think "Acme needs two things" — the
 * company is the unit they are responsible for, and an obligation is always an
 * obligation *of a company*. Grouping is therefore structural, not decorative:
 * it matches the shape of the data and the shape of the responsibility.
 *
 * The flat table this replaces scattered one company's three documents across
 * three pages of a sorted list, so a director settling Acme had to find its
 * rows among every other company's.
 */

export interface CompanyGroup {
  companyId: string;
  companyName: string;
  items: OutstandingItem[];
  /** Totals over the group, so a header can state what the company needs
   *  without its cards being on screen. */
  amountDue: number;
  toSign: number;
  toPay: number;
  /** Items this director has already done their part on, still waiting on a
   *  co-director. Counted so the closed header can say so — otherwise the one
   *  state a director cannot act on is also the one they have to expand a
   *  group to discover. */
  waiting: number;
  hasOverdue: boolean;
}

/** What a single item is waiting on. Drives the card's icon and its wizard step. */
export type ItemNeed = 'signAndPay' | 'sign' | 'pay' | 'waiting';

export const needOf = (item: OutstandingItem): ItemNeed => {
  const mustSign = needsMySignature(item);
  const mustPay = isUnpaid(item) && isPayable(item);

  if (mustSign && mustPay) return 'signAndPay';
  if (mustSign) return 'sign';
  if (mustPay) return 'pay';
  return 'waiting';
};

/** True when this director still has something to do on the item. A row waiting
 *  on co-directors is not their task and must not be counted as one. */
export const isActionable = (item: OutstandingItem): boolean => needOf(item) !== 'waiting';

/**
 * Groups items by company, preserving the urgency order the API layer applied.
 *
 * Companies are ordered by their most urgent item rather than alphabetically:
 * the group holding an overdue annual review belongs at the top of the screen,
 * not wherever its name happens to fall. Insertion order does that for free —
 * `outstanding.api` already sorts by urgency, so the first company to appear is
 * the one owning the most urgent item.
 */
export const groupByCompany = (items: OutstandingItem[]): CompanyGroup[] => {
  const groups = new Map<string, CompanyGroup>();

  for (const item of items) {
    // Keying on the id, not the name: two companies can share a name, and
    // collapsing them would merge one director's obligations with another's.
    const existing = groups.get(item.asicCompanyId);
    const group = existing ?? {
      companyId: item.asicCompanyId,
      companyName: item.asicCompanyName,
      items: [],
      amountDue: 0,
      toSign: 0,
      toPay: 0,
      waiting: 0,
      hasOverdue: false,
    };

    const need = needOf(item);
    group.items.push(item);
    group.amountDue += amountDueOf(item);
    group.toSign += need === 'sign' || need === 'signAndPay' ? 1 : 0;
    group.toPay += need === 'pay' || need === 'signAndPay' ? 1 : 0;
    group.waiting += need === 'waiting' ? 1 : 0;
    group.hasOverdue = group.hasOverdue || isOverdue(item);

    if (existing === undefined) groups.set(item.asicCompanyId, group);
  }

  return [...groups.values()];
};

/**
 * One step of the guided flow.
 *
 * An item that needs both a signature and a payment becomes two steps. They are
 * genuinely two decisions — a director may sign a resolution and still want to
 * pay it on a different card — and a wizard that asked for both at once would
 * be the dialog it replaces.
 */
export interface FlowStep {
  /** Unique across the queue; an item contributing two steps yields two ids. */
  id: string;
  kind: 'sign' | 'pay';
  item: OutstandingItem;
}

/**
 * The queue the guided flow walks, in the order it walks it.
 *
 * Signing precedes paying **on the same item**: the solvency resolution is what
 * authorises the annual review, and paying for a document you have not read is
 * the wrong order to be asked in. Across items, urgency order is preserved, so
 * the overdue company is cleared first.
 */
export const buildFlowQueue = (items: OutstandingItem[]): FlowStep[] => {
  const steps: FlowStep[] = [];

  for (const item of items) {
    const need = needOf(item);
    if (need === 'sign' || need === 'signAndPay') {
      steps.push({ id: `${item.id}:sign`, kind: 'sign', item });
    }
    if (need === 'pay' || need === 'signAndPay') {
      steps.push({ id: `${item.id}:pay`, kind: 'pay', item });
    }
  }

  return steps;
};

/**
 * One company's run of consecutive steps within the frozen queue.
 *
 * The queue is already in urgency order and a company's items sit together in
 * it, so a run is a contiguous slice rather than a regrouping — the walking
 * order is never changed to draw it.
 */
export interface FlowChapter {
  companyId: string;
  companyName: string;
  steps: FlowStep[];
  /** Index in the queue of this chapter's first step. Lets the rail map a
   *  position back to the chapter holding it without a second lookup. */
  startIndex: number;
}

/**
 * Splits the queue into per-company chapters.
 *
 * Boundaries are drawn where the company id changes rather than by collecting
 * every step per company: two separate runs for one company would mean the
 * urgency order put another company's work between them, and merging them in
 * the rail would misreport where the director actually is.
 */
export const chaptersOf = (steps: FlowStep[]): FlowChapter[] => {
  const chapters: FlowChapter[] = [];

  steps.forEach((step, index) => {
    const open = chapters[chapters.length - 1];

    if (open?.companyId === step.item.asicCompanyId) {
      open.steps.push(step);
      return;
    }

    chapters.push({
      companyId: step.item.asicCompanyId,
      companyName: step.item.asicCompanyName,
      steps: [step],
      startIndex: index,
    });
  });

  return chapters;
};

/** What a chapter is doing while the flow runs. `sealed` is the state the rail
 *  exists to show: every step resolved, with nothing skipped. */
export type ChapterState = 'sealed' | 'partial' | 'active' | 'pending';

export const chapterStateOf = (
  chapter: FlowChapter,
  index: number,
  outcomes: Map<string, 'done' | 'skipped'>,
): ChapterState => {
  const resolved = chapter.steps.filter((step) => outcomes.has(step.id));

  if (resolved.length === chapter.steps.length) {
    // A skip leaves the company still owing something, so the chapter closes
    // as partial rather than sealed — sealing it would tell the director the
    // company was clear when an item is still on their list.
    return resolved.every((step) => outcomes.get(step.id) === 'done') ? 'sealed' : 'partial';
  }

  const end = chapter.startIndex + chapter.steps.length;
  return index >= chapter.startIndex && index < end ? 'active' : 'pending';
};
